'use client';

import { useState } from 'react';
import { consumeToolUsage, formatToolCreditSuccess } from '@/lib/browser-tool-usage';
import {
  buildArticleSchema,
  buildFaqSchema,
  buildProductSchema,
  createSeoJson,
  type ArticleSchemaInput,
  type FaqSchemaItem,
  type ProductSchemaInput,
} from '@/lib/browser-seo-tools';

type Variant = 'faq-schema-builder' | 'article-schema-builder' | 'product-schema-builder';

type ToolResult =
  | {
      kind: 'faq';
      jsonText: string;
      itemCount: number;
    }
  | {
      kind: 'article';
      jsonText: string;
      schemaType: string;
    }
  | {
      kind: 'product';
      jsonText: string;
      hasOffer: boolean;
      hasRating: boolean;
    };

const initialFaqItems: FaqSchemaItem[] = [
  { question: '', answer: '' },
  { question: '', answer: '' },
];

const initialArticleInput: ArticleSchemaInput = {
  articleType: 'Article',
  headline: '',
  description: '',
  url: '',
  image: '',
  author: '',
  publisher: '',
  datePublished: '',
  dateModified: '',
};

const initialProductInput: ProductSchemaInput = {
  name: '',
  description: '',
  url: '',
  image: '',
  brand: '',
  sku: '',
  price: '',
  currency: 'USD',
  availability: 'https://schema.org/InStock',
  ratingValue: '',
  ratingCount: '',
};

const toolCopy: Record<
  Variant,
  {
    title: string;
    description: string;
    badge: string;
    actionLabel: string;
    successLabel: string;
    downloadName: string;
  }
> = {
  'faq-schema-builder': {
    title: 'Build FAQPage JSON-LD from practical question and answer rows',
    description:
      'Add one or more FAQ items, generate clean JSON-LD locally, and keep the output ready for copy or download.',
    badge: 'FAQ',
    actionLabel: 'Build FAQ schema',
    successLabel: 'FAQ schema build',
    downloadName: 'faq-schema.json',
  },
  'article-schema-builder': {
    title: 'Build Article or BlogPosting JSON-LD locally',
    description:
      'Fill the practical article metadata fields, generate clean JSON-LD, and keep the result ready for schema review.',
    badge: 'Article',
    actionLabel: 'Build article schema',
    successLabel: 'Article schema build',
    downloadName: 'article-schema.json',
  },
  'product-schema-builder': {
    title: 'Build one practical Product JSON-LD block',
    description:
      'Enter the core product fields, generate one JSON-LD block locally, and keep the output narrow and marketplace-friendly.',
    badge: 'Product',
    actionLabel: 'Build product schema',
    successLabel: 'Product schema build',
    downloadName: 'product-schema.json',
  },
};

function createTextDownload(
  text: string,
  fileName: string,
  mimeType = 'application/ld+json;charset=utf-8',
) {
  const url = URL.createObjectURL(new Blob([text], { type: mimeType }));
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function copyText(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  throw new Error('The browser could not access the clipboard.');
}

export default function BrowserSchemaBuilder({ variant }: { variant: Variant }) {
  const copy = toolCopy[variant];
  const [faqItems, setFaqItems] = useState<FaqSchemaItem[]>(initialFaqItems);
  const [articleInput, setArticleInput] = useState<ArticleSchemaInput>(initialArticleInput);
  const [productInput, setProductInput] = useState<ProductSchemaInput>(initialProductInput);
  const [result, setResult] = useState<ToolResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);

  function resetOutput() {
    setResult(null);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);
  }

  function updateFaqItem(index: number, key: keyof FaqSchemaItem, value: string) {
    setFaqItems((current) =>
      current.map((item, itemIndex) => (itemIndex === index ? { ...item, [key]: value } : item)),
    );
    resetOutput();
  }

  function addFaqItem() {
    setFaqItems((current) => [...current, { question: '', answer: '' }]);
    resetOutput();
  }

  function removeFaqItem(index: number) {
    setFaqItems((current) =>
      current.length > 1 ? current.filter((_, itemIndex) => itemIndex !== index) : current,
    );
    resetOutput();
  }

  function updateArticleInput(key: keyof ArticleSchemaInput, value: string) {
    setArticleInput((current) => ({ ...current, [key]: value }));
    resetOutput();
  }

  function updateProductInput(key: keyof ProductSchemaInput, value: string) {
    setProductInput((current) => ({ ...current, [key]: value }));
    resetOutput();
  }

  async function buildSchema() {
    setIsRunning(true);
    setErrorMessage(null);
    setCreditMessage(null);
    setCopyMessage(null);

    try {
      const credits = await consumeToolUsage(
        variant,
        'Credits are not available for another schema build right now.',
      );

      if (variant === 'faq-schema-builder') {
        const schema = buildFaqSchema(faqItems);
        setResult({
          kind: 'faq',
          jsonText: createSeoJson(schema),
          itemCount: schema.mainEntity.length,
        });
      } else if (variant === 'article-schema-builder') {
        const schema = buildArticleSchema(articleInput);
        setResult({
          kind: 'article',
          jsonText: createSeoJson(schema),
          schemaType: schema['@type'],
        });
      } else {
        const schema = buildProductSchema(productInput);
        setResult({
          kind: 'product',
          jsonText: createSeoJson(schema),
          hasOffer: Boolean(schema.offers),
          hasRating: Boolean(schema.aggregateRating),
        });
      }

      setCreditMessage(formatToolCreditSuccess(copy.successLabel, credits));
    } catch (error) {
      setResult(null);
      setErrorMessage(error instanceof Error ? error.message : 'The schema could not be built.');
    } finally {
      setIsRunning(false);
    }
  }

  async function handleCopy() {
    if (!result) {
      return;
    }

    try {
      await copyText(result.jsonText);
      setCopyMessage('JSON-LD copied.');
      setErrorMessage(null);
    } catch {
      setErrorMessage('The browser could not copy the current schema.');
    }
  }

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">{copy.badge}</span>
        <h2 className="tool-live-title">{copy.title}</h2>
        <p className="tool-live-subtitle">{copy.description}</p>
      </div>

      <div className="tool-live-grid">
        <section className="tool-live-uploader">
          {variant === 'faq-schema-builder' ? (
            <div className="tool-live-controls">
              {faqItems.map((item, index) => (
                <div className="tool-live-field tool-live-field-full" key={`faq-${index}`}>
                  <div className="tool-live-actions" style={{ justifyContent: 'space-between' }}>
                    <span>FAQ item {index + 1}</span>
                    {faqItems.length > 1 ? (
                      <button
                        className="button button-secondary"
                        onClick={() => removeFaqItem(index)}
                        type="button"
                      >
                        Remove
                      </button>
                    ) : null}
                  </div>
                  <input
                    onChange={(event) => updateFaqItem(index, 'question', event.target.value)}
                    placeholder="What does this tool check?"
                    type="text"
                    value={item.question}
                  />
                  <textarea
                    onChange={(event) => updateFaqItem(index, 'answer', event.target.value)}
                    placeholder="It checks the pasted content locally in the browser and outputs practical JSON-LD."
                    value={item.answer}
                  />
                </div>
              ))}

              <div className="tool-live-actions">
                <button className="button button-secondary" onClick={addFaqItem} type="button">
                  Add FAQ row
                </button>
              </div>
            </div>
          ) : null}

          {variant === 'article-schema-builder' ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>Schema type</span>
                <select
                  onChange={(event) =>
                    updateArticleInput(
                      'articleType',
                      event.target.value as ArticleSchemaInput['articleType'],
                    )
                  }
                  value={articleInput.articleType}
                >
                  <option value="Article">Article</option>
                  <option value="BlogPosting">BlogPosting</option>
                </select>
              </label>

              <label className="tool-live-field">
                <span>Headline</span>
                <input
                  onChange={(event) => updateArticleInput('headline', event.target.value)}
                  placeholder="Browser-first SEO metadata tools"
                  type="text"
                  value={articleInput.headline}
                />
              </label>

              <label className="tool-live-field tool-live-field-full">
                <span>Description</span>
                <textarea
                  onChange={(event) => updateArticleInput('description', event.target.value)}
                  placeholder="A practical browser-first toolkit for metadata review, schema generation, and publishing cleanup."
                  value={articleInput.description}
                />
              </label>

              <label className="tool-live-field">
                <span>URL</span>
                <input
                  onChange={(event) => updateArticleInput('url', event.target.value)}
                  placeholder="https://cleanor.app/tools/article-schema-builder"
                  spellCheck={false}
                  type="text"
                  value={articleInput.url}
                />
              </label>

              <label className="tool-live-field">
                <span>Image URL</span>
                <input
                  onChange={(event) => updateArticleInput('image', event.target.value)}
                  placeholder="https://cleanor.app/og/article-schema.png"
                  spellCheck={false}
                  type="text"
                  value={articleInput.image}
                />
              </label>

              <label className="tool-live-field">
                <span>Author</span>
                <input
                  onChange={(event) => updateArticleInput('author', event.target.value)}
                  placeholder="Igor Shenshin"
                  type="text"
                  value={articleInput.author}
                />
              </label>

              <label className="tool-live-field">
                <span>Publisher</span>
                <input
                  onChange={(event) => updateArticleInput('publisher', event.target.value)}
                  placeholder="Cleanor"
                  type="text"
                  value={articleInput.publisher}
                />
              </label>

              <label className="tool-live-field">
                <span>Date published</span>
                <input
                  onChange={(event) => updateArticleInput('datePublished', event.target.value)}
                  type="date"
                  value={articleInput.datePublished}
                />
              </label>

              <label className="tool-live-field">
                <span>Date modified</span>
                <input
                  onChange={(event) => updateArticleInput('dateModified', event.target.value)}
                  type="date"
                  value={articleInput.dateModified}
                />
              </label>
            </div>
          ) : null}

          {variant === 'product-schema-builder' ? (
            <div className="tool-live-controls">
              <label className="tool-live-field">
                <span>Product name</span>
                <input
                  onChange={(event) => updateProductInput('name', event.target.value)}
                  placeholder="Cleanor Metadata Toolkit"
                  type="text"
                  value={productInput.name}
                />
              </label>

              <label className="tool-live-field">
                <span>Product URL</span>
                <input
                  onChange={(event) => updateProductInput('url', event.target.value)}
                  placeholder="https://cleanor.app/tools/product-schema-builder"
                  spellCheck={false}
                  type="text"
                  value={productInput.url}
                />
              </label>

              <label className="tool-live-field tool-live-field-full">
                <span>Description</span>
                <textarea
                  onChange={(event) => updateProductInput('description', event.target.value)}
                  placeholder="A practical browser-first SEO toolkit for publishing, metadata cleanup, and schema generation."
                  value={productInput.description}
                />
              </label>

              <label className="tool-live-field">
                <span>Image URL</span>
                <input
                  onChange={(event) => updateProductInput('image', event.target.value)}
                  placeholder="https://cleanor.app/og/product-schema.png"
                  spellCheck={false}
                  type="text"
                  value={productInput.image}
                />
              </label>

              <label className="tool-live-field">
                <span>Brand</span>
                <input
                  onChange={(event) => updateProductInput('brand', event.target.value)}
                  placeholder="Cleanor"
                  type="text"
                  value={productInput.brand}
                />
              </label>

              <label className="tool-live-field">
                <span>SKU</span>
                <input
                  onChange={(event) => updateProductInput('sku', event.target.value)}
                  placeholder="SEO-TOOLKIT-001"
                  type="text"
                  value={productInput.sku}
                />
              </label>

              <label className="tool-live-field">
                <span>Price</span>
                <input
                  inputMode="decimal"
                  onChange={(event) => updateProductInput('price', event.target.value)}
                  placeholder="49.00"
                  type="text"
                  value={productInput.price}
                />
              </label>

              <label className="tool-live-field">
                <span>Currency</span>
                <input
                  onChange={(event) => updateProductInput('currency', event.target.value)}
                  placeholder="USD"
                  spellCheck={false}
                  type="text"
                  value={productInput.currency}
                />
              </label>

              <label className="tool-live-field">
                <span>Availability</span>
                <select
                  onChange={(event) => updateProductInput('availability', event.target.value)}
                  value={productInput.availability}
                >
                  <option value="https://schema.org/InStock">InStock</option>
                  <option value="https://schema.org/OutOfStock">OutOfStock</option>
                  <option value="https://schema.org/PreOrder">PreOrder</option>
                </select>
              </label>

              <label className="tool-live-field">
                <span>Rating value</span>
                <input
                  inputMode="decimal"
                  onChange={(event) => updateProductInput('ratingValue', event.target.value)}
                  placeholder="4.8"
                  type="text"
                  value={productInput.ratingValue}
                />
              </label>

              <label className="tool-live-field">
                <span>Rating count</span>
                <input
                  inputMode="numeric"
                  onChange={(event) => updateProductInput('ratingCount', event.target.value)}
                  placeholder="128"
                  type="text"
                  value={productInput.ratingCount}
                />
              </label>
            </div>
          ) : null}

          <div className="tool-live-actions">
            <button
              className="button"
              disabled={isRunning}
              onClick={() => void buildSchema()}
              type="button"
            >
              {isRunning ? 'Building…' : copy.actionLabel}
            </button>
          </div>

          {errorMessage ? <p className="status-message status-error">{errorMessage}</p> : null}
          {creditMessage ? <p className="status-message status-success">{creditMessage}</p> : null}
          {copyMessage ? <p className="status-message status-success">{copyMessage}</p> : null}
        </section>

        <aside className="tool-live-panel">
          {result ? (
            <>
              <div className="tool-live-actions">
                <button
                  className="button button-secondary"
                  onClick={() => void handleCopy()}
                  type="button"
                >
                  Copy JSON-LD
                </button>
                <button
                  className="button button-secondary"
                  onClick={() => createTextDownload(result.jsonText, copy.downloadName)}
                  type="button"
                >
                  Download JSON
                </button>
              </div>

              {result.kind === 'faq' ? (
                <div className="tool-live-stats">
                  <div>
                    <strong>Schema type</strong>
                    <span>FAQPage</span>
                  </div>
                  <div>
                    <strong>Questions</strong>
                    <span>{result.itemCount}</span>
                  </div>
                </div>
              ) : null}

              {result.kind === 'article' ? (
                <div className="tool-live-stats">
                  <div>
                    <strong>Schema type</strong>
                    <span>{result.schemaType}</span>
                  </div>
                  <div>
                    <strong>Author</strong>
                    <span>{articleInput.author.trim() || 'Not set'}</span>
                  </div>
                </div>
              ) : null}

              {result.kind === 'product' ? (
                <div className="tool-live-stats">
                  <div>
                    <strong>Offer block</strong>
                    <span>{result.hasOffer ? 'Included' : 'Missing'}</span>
                  </div>
                  <div>
                    <strong>Rating block</strong>
                    <span>{result.hasRating ? 'Included' : 'Missing'}</span>
                  </div>
                </div>
              ) : null}

              <div className="tool-live-preview">
                <h3>Generated JSON-LD</h3>
                <pre className="tool-markdown-code">{result.jsonText}</pre>
              </div>
            </>
          ) : (
            <div className="tool-live-empty">
              <p>Build the schema to preview one clean JSON-LD block here.</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
